import { CommonModule, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  menuItem,
  requestFn,
  showRequestErrors,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import {
  DownloadStore,
  FileService,
  SOFTLINE_FEATURE_DOWNLOAD,
  Store,
} from '@softline/core';
import {
  ChipComponent,
  LoadingSkeletonComponent,
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import { combineLatestWith, Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { StationChangedPipe } from '../../pipes/station-changed.pipe';
import { AuftragStore, StationStore } from '../../store';
import { SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_DOWNLOAD } from '../../transport.api';
import {
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
} from '../../transport.shared';
import { Auftrag } from '../../types/auftrag';
import { AuftragDetail } from '../../types/auftrag-detail';
import { KopfauBean } from '../../types/kopfau-bean';
import { A11yModule } from "@angular/cdk/a11y";

@Component({
  selector: 'soft-auftrag-detail',
  imports: [
    CommonModule,
    StationChangedPipe,
    ChipComponent,
    LoadingSkeletonComponent,
    PageComponent,
    PageHeaderComponent,
    TranslatePipe,
    A11yModule
],
  templateUrl: './auftrag-detail.component.html',
  styleUrls: ['./auftrag-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuftragDetailComponent implements OnInit, OnDestroy, BackNavigable {
  private store = inject(Store);
  protected router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private viewportScroller = inject(ViewportScroller);
  private fileService = inject(FileService);
  private backNavigationService = inject(BackNavigationService);
  private subscription?: Subscription;
  private auftragSubscription?: Subscription;

  private t = translator();
  private request = requestFn();

  private readonly id$ = this.activatedRoute.paramMap.pipe(
    map((params) => +(params.get('id') ?? 0))
  );

  readonly auftrag$: Observable<Auftrag & AuftragDetail> = this.id$.pipe(
    switchMap((id) =>
      this.store.observe(
        SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
        AuftragStore.getters.entityWithDetail,
        id
      )
    )
  );

  readonly stationen$: Observable<KopfauBean[]> = this.store
    .observe(SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN, StationStore.getters.all)
    .pipe(
      combineLatestWith(this.id$),
      map(([stationen, id]) =>
        stationen
          .filter((o) => o.idkopfau === id)
          .sort((a, b) => (a.lfdnr > b.lfdnr ? 1 : -1))
      )
    );

  readonly loading$: Observable<boolean> = this.store.observe(
    SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
    AuftragStore.getters.loading,
  );

  constructor() {
    menuItem(() => ({
      title: '#TRANSPORT.ORDER_DETAIL.SUBMIT',
      type: 'execute',
      canExecute: true,
      outlet: 'page-bottom',
      class: 'soft-button primary',
      name: 'completeOrder',
      execute: async () => {
        const auftrag = this.store.get(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.getters.entityWithDetail,
          +(this.activatedRoute.snapshot.paramMap.get('id') ?? 0),
        );
        const stationen = this.store.get(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
          StationStore.getters.all,
        );
        if (auftrag && stationen) {
          await this.endOrder(auftrag as Auftrag & AuftragDetail, stationen);
        }
      },
    }));
  }

  ngOnInit(): void {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      'Energietransport'
    );
    this.backNavigationService.set(this);
    this.auftragSubscription = this.auftrag$.subscribe((o) => {
      this.store.commit(
        SOFTLINE_FEATURE_TITLE,
        TitleStore.mutations.setSubTitle,
        `Transportauftrag ${o?.nummer}`
      );
    });

    // scroll to top
    this.viewportScroller.scrollToPosition([0, 0]);
    const content = document?.getElementsByTagName('soft-pull-to-refresh-container');
    content[0].scrollTop = 0;

    this.subscription = this.id$.subscribe(async (id) => {
      if (id === null) return;
      try {
        const entity = this.store.get(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.getters.entity,
          id,
        );
        if (!entity) {
          await this.store.dispatch(
            SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
            AuftragStore.actions.loadMany,
            { clear: true },
          );
        }
        const reloaded = this.store.get(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.getters.entity,
          id,
        );
        if (!reloaded) {
          await this.router.navigate(['/energietransport']);
          return;
        }
        await this.store.dispatch(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.actions.loadDetail,
          { id },
        );
      } catch (e) {
        showRequestErrors(this.store, e);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;

    if (this.auftragSubscription && !this.auftragSubscription.closed)
      this.auftragSubscription.unsubscribe();
    this.auftragSubscription = undefined;

    this.backNavigationService.set(undefined);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/energietransport']);
  }

  async printAuftrag(auftrag: Auftrag, station: KopfauBean): Promise<void> {
    const filename = `Auftrag_${station?.belegnummer ?? station.idkopflf}.pdf`;

    const messageBarId = await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.loading
    );
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_DOWNLOAD,
        DownloadStore.actions.download,
        {
          location: {
            path: SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_DOWNLOAD,
            pathParams: { id: auftrag.id, idkopflf: station.idkopflf },
          },
          filename,
        }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.close,
        messageBarId
      );
      await this.fileService.openBlob(result.content, filename);
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.close,
        messageBarId
      );
      showRequestErrors(this.store, e);
    }
  }

  async endOrder(
    auftrag: Auftrag & AuftragDetail,
    stationen: KopfauBean[]
  ): Promise<void> {
    const stationCount = stationen.length;
    const completedStations = stationen.filter(
      (station) => station?.idkopflf !== null
    )?.length;
    const count = stationCount - completedStations;

    if (count) {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_MODAL,
        ModalStore.actions.ask,
        {
          title:
            count === 1
              ? '#TRANSPORT.DIALOGS.COMPLETE_TRANSPORT_CONFIRM.TITLE_SG'
              : '#TRANSPORT.DIALOGS.COMPLETE_TRANSPORT_CONFIRM.TITLE_PL',
          question:
            count === 1
              ? '#TRANSPORT.DIALOGS.COMPLETE_TRANSPORT_CONFIRM.MESSAGE_SG'
              : '#TRANSPORT.DIALOGS.COMPLETE_TRANSPORT_CONFIRM.MESSAGE_PL',
          params: { count },
          dismiss: { backdrop: true, escape: true },
        }
      );
      if (result !== 'YES') return;
    }

    const result = await this.request({
      options: {
        type: 'saving',
        successMessage: this.t('Der Auftrag wurde erfolgreich abgeschlossen'),
        savingMessage: this.t('Auftrag wird abgeschlossen...'),
      },
      request: async () => {
        const value = this.store.get(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.getters.entity,
          auftrag.id
        );
        await this.store.dispatch(
          SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
          AuftragStore.actions.complete,
          { auftrag: value }
        );
        return true;
      },
    });
    if (result) await this.navigateBack();
  }

  async openStationDetails(
    station: KopfauBean,
    mode: 'edit' | 'readonly'
  ): Promise<void> {
    const id = +(this.activatedRoute.snapshot.paramMap.get('id') ?? 0);
    await this.router.navigate(['/energietransport', id, 'station', station.id], {
      queryParams: { mode },
    });
  }
}
